import { fork, put, takeLatest, takeEvery, select } from "redux-saga/effects";
import {
  fetchApplications,
  fetchApplication,
  createApplication,
  updateApplication,
  deleteApplication,
  generateClientSecret,
  testNotifications,
  setOutstandingNotifications,
  fetchOutstandingNotifications,
  setNotificationsSettings,
  fetchNotificationsSettings,
  updateNotificationsSettings,
  generateNotificationsSecret,
  updateNotificationStatus,
  setNotificationTestResponse,
  setList,
  setItem,
  removeListItem,
  replaceListItem,
  setApplicationActiveFlag,
  setFailedNotifications,
  fetchFailedNotifications,
} from "../store/applicationsSlice";

import { push } from "connected-react-router";

import {
  startLoading,
  setError,
  setSuccess,
  enqueueSnackbarSuccess,
  enqueueSnackbarError,
} from "../store/appSlice";

import { selectCurrentCustomerId } from "../store/authSlice";

import { callApi } from "./helpers/api";
import * as ClientsApi from "../api/Clients";
import * as ClientNotificationsApi from "../api/ClientNotifications";
import { PayloadAction } from "@reduxjs/toolkit";
import { UUID } from "../utils";
import Client from "../models/Client";
// import * as ClientNotificationsFakeApi from "../fakeApi/ClientNotifications";

function* fetchApplicationsSaga({
  payload = {},
}: PayloadAction<{ search?: string } | undefined>) {
  const { search } = payload;

  const operationPath = "applications.list";
  yield put(startLoading(operationPath));
  try {
    const { list } = yield callApi(ClientsApi.getClients, { name: search });
    yield put(setList(list));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchApplicationSaga({ payload }: PayloadAction<{ id: UUID }>) {
  const { id } = payload;

  const operationPath = ["applications", "items", id];
  yield put(startLoading(operationPath));

  try {
    const application = yield callApi(ClientsApi.getClient, id);
    yield put(setItem(application));
    yield put(replaceListItem(application));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createApplicationSaga({ payload }: PayloadAction<{ data: Client }>) {
  const { data } = payload;
  const operationPath = ["applications", "create"];
  yield put(startLoading(operationPath));
  const customerId = yield select(selectCurrentCustomerId);
  try {
    const application = yield callApi(ClientsApi.postClient, {
      ...data,
      customer: customerId,
    });
    yield put(setItem(application));
    yield put(fetchApplications());
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/applications/${application.id}`));
    yield put(
      enqueueSnackbarSuccess({
        code: "Application created",
      })
    );
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateApplicationSaga({
  payload,
}: PayloadAction<{ id: UUID; data: Client }>) {
  const { id, data } = payload;
  const operationPath = ["applications", "items", id, "update"];
  yield put(startLoading(operationPath));

  try {
    const application = yield callApi(ClientsApi.patchClient, id, data);
    yield put(setItem(application));
    yield put(replaceListItem(application));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/applications/${application.id}`));
    yield put(
      enqueueSnackbarSuccess({
        code: "Application updated",
      })
    );
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* deleteApplicationSaga({
  payload: { id },
}: PayloadAction<{ id: UUID }>) {
  try {
    yield callApi(ClientsApi.deleteClient, id);
    yield put(removeListItem(id));
    yield put(fetchApplications());
    yield put(push(`/configuration/applications`));
    yield put(
      enqueueSnackbarSuccess({
        code: "Application removed",
      })
    );
  } catch (error) {
    console.error(error);
    yield put(
      enqueueSnackbarError({
        code: "Removing application failed",
      })
    );
  }
}

function* generateClientSecretSaga({ payload: id }: PayloadAction<UUID>) {
  const operationPath = ["applications", "items", id, "generateClientSecret"];
  yield put(startLoading(operationPath));

  try {
    const application = yield callApi(ClientsApi.regenerateSecret, id);
    yield put(setItem(application));
    yield put(replaceListItem(application));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* testNotificationsSaga({ payload }: PayloadAction<{ id: UUID }>) {
  const { id } = payload;
  const operationPath = ["applications", "items", id, "testNotifications"];
  yield put(startLoading(operationPath));

  try {
    const data = yield callApi(
      ClientNotificationsApi.postNotificationsTest,
      id
    );
    yield put(updateNotificationStatus({ id, data }));
    yield put(setNotificationTestResponse({ data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchOutstandingNotificationsSaga({ payload }: PayloadAction<UUID>) {
  const applicationId = payload;
  const operationPath = [
    "applications",
    "items",
    applicationId,
    "notifications",
  ];
  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(
      ClientNotificationsApi.getOutstandingNotifications,
      applicationId
    );
    yield put(setOutstandingNotifications({ id: applicationId, data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchFailedNotificationsSaga({
  payload,
}: PayloadAction<{ id: UUID }>) {
  const { id: applicationId } = payload;
  const operationPath = ["notifications", "failed", applicationId];
  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(
      ClientNotificationsApi.getFailedNotifications,
      applicationId
    );
    yield put(setFailedNotifications({ id: applicationId, data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchNotificationsSettingsSaga({
  payload,
}: PayloadAction<{ id: UUID }>) {
  const { id } = payload;
  const operationPath = ["applications", "items", id, "notificationsSettings"];
  yield put(startLoading(operationPath));
  try {
    const data = yield callApi(
      ClientNotificationsApi.getNotificationsSettings,
      id
    );
    yield put(setNotificationsSettings({ id, data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateNotificationsSettingsSaga({
  payload,
}: PayloadAction<{ id: UUID; data: any }>) {
  const { id, data } = payload;
  const operationPath = ["applications", "items", id, "notificationsSettings"];
  yield put(startLoading(operationPath));

  try {
    const notificationsSettings = yield callApi(
      ClientNotificationsApi.postNotificationsSettings,
      id,
      data
    );
    yield put(setNotificationsSettings({ id, notificationsSettings }));
    yield put(setSuccess(operationPath));
    yield put(push(`/configuration/applications/${id}`));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* setApplicationActiveFlagSaga({
  payload,
}: PayloadAction<{ id: UUID; value: boolean }>) {
  const { id, value } = payload;
  const operationPath = [
    "applications",
    "items",
    id,
    "setApplicationActiveFlag",
  ];
  yield put(startLoading(operationPath));
  try {
    const application = yield callApi(ClientsApi.patchClient, id, {
      active: value,
    });
    yield put(setItem(application));
    yield put(replaceListItem(application));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* generateNotificationsSecretSaga({
  payload: { id },
}: PayloadAction<{ id: UUID }>) {
  const operationPath = [
    "applications",
    "items",
    id,
    "generateNotificationsSecret",
  ];
  yield put(startLoading(operationPath));

  try {
    const data = yield callApi(ClientNotificationsApi.regenerateSecret, id);
    yield put(setNotificationsSettings({ id, data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchApplications() {
  yield takeLatest(fetchApplications.type, fetchApplicationsSaga);
}

function* watchFetchApplication() {
  yield takeLatest(fetchApplication.type, fetchApplicationSaga);
}

function* watchCreateApplication() {
  yield takeEvery(createApplication.type, createApplicationSaga);
}

function* watchUpdateApplication() {
  yield takeEvery(updateApplication.type, updateApplicationSaga);
}

function* watchDeleteApplication() {
  yield takeEvery(deleteApplication.type, deleteApplicationSaga);
}

function* watchGenerateClientSecret() {
  yield takeEvery(generateClientSecret.type, generateClientSecretSaga);
}

function* watchTestNotifications() {
  yield takeEvery(testNotifications.type, testNotificationsSaga);
}

function* watchFetchNotificationsSettings() {
  yield takeEvery(
    fetchNotificationsSettings.type,
    fetchNotificationsSettingsSaga
  );
}

function* watchUpdateNotificationsSettings() {
  yield takeEvery(
    updateNotificationsSettings.type,
    updateNotificationsSettingsSaga
  );
}

function* watchFetchOutstandingNotifications() {
  yield takeEvery(
    fetchOutstandingNotifications.type,
    fetchOutstandingNotificationsSaga
  );
}

function* watchFailedFetchNotifications() {
  yield takeEvery(fetchFailedNotifications.type, fetchFailedNotificationsSaga);
}

function* watchGenerateNotificationsSecretSaga() {
  yield takeEvery(
    generateNotificationsSecret.type,
    generateNotificationsSecretSaga
  );
}

function* watchSetApplicationActiveFlag() {
  yield takeEvery(setApplicationActiveFlag.type, setApplicationActiveFlagSaga);
}

export default function* rootSaga() {
  yield fork(watchFetchApplications);
  yield fork(watchFetchApplication);
  yield fork(watchCreateApplication);
  yield fork(watchUpdateApplication);
  yield fork(watchGenerateClientSecret);
  yield fork(watchTestNotifications);
  yield fork(watchFetchOutstandingNotifications);
  yield fork(watchDeleteApplication);
  yield fork(watchSetApplicationActiveFlag);
  yield fork(watchFetchNotificationsSettings);
  yield fork(watchFailedFetchNotifications);
  yield fork(watchUpdateNotificationsSettings);
  yield fork(watchGenerateNotificationsSecretSaga);
}
